/**
 * SDK-agnostic schema and path helpers for the `mail` Firestore collection.
 *
 * The `mail` collection is consumed by the Firebase Trigger Email extension
 * (https://firebase.google.com/docs/extensions/official/firestore-send-email).
 * Each document triggers the extension to send one email. This module owns
 * the canonical Firestore document shape and is the single source of truth for
 * all consumers (functions, email-queue worker, webapp read paths, tooling).
 *
 * ## Caller-writeable vs. persisted split
 *
 * Three types model the `/mail` document at different points in its lifecycle:
 *
 * - **{@link MailDocumentInput}** — the caller-writeable surface. Callers
 *   (enqueue API, `sendEmail`, `sendEmailBatch`, reminder cron) construct and
 *   pass values of this type. It contains only the envelope fields (`to`,
 *   `from`, `cc`, `bcc`, `template`, `message`) and the caller-controlled
 *   portion of `cipMetadata` (`courseId`, `additionalFields`). The input
 *   schema is **strict** — it rejects top-level `delivery` and the
 *   system-managed `cipMetadata` fields (`batchId`, `jobId`, `itemIndex`,
 *   `campaignId`).
 *
 * - **{@link MailDocumentAgentInput}** — the same surface plus the two
 *   privileged reply fields (`replyTo`, `headers`). It exists as a separate
 *   type precisely so those fields stay off `MailDocumentInput`, which is the
 *   request-validation allowlist for the enqueue callables. See
 *   {@link MailDocumentAgentInputSchema}.
 *
 * - **{@link MailDocument}** — the persisted form. Adds `delivery.*` (written
 *   by the Firebase Trigger Email extension), the privileged reply fields, and
 *   extends `cipMetadata` with `batchId`, `jobId`, and `itemIndex` (written by
 *   the pipeline worker and `sendEmailBatch`). The persisted schema uses
 *   `.loose()` to tolerate unknown extension-managed fields. It is a
 *   structural superset of `MailDocumentInput`.
 *
 * The enqueue boundary validates incoming records against
 * `MailDocumentInputSchema`, ensuring callers cannot pre-populate
 * `delivery.*` or system `cipMetadata` fields.
 *
 * @module
 */

import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';
import type {UniqueStringId} from '../../types/branded';
import type {CourseId} from '../../types/branded';
import type {EmailBatchId} from './emailBatch';

// ============================================================================
// Collection constant and branded ID
// ============================================================================

/**
 * Firestore collection name for outbound mail documents.
 * Every document written here is picked up by the firestore-send-email
 * extension and triggers a delivery attempt.
 */
export const MAIL_COLLECTION = 'mail' as const;

/**
 * Branded string ID for a document in the `mail` collection.
 * Replaces the legacy `SentTemplatedEmailId` alias.
 */
export type MailDocumentId = UniqueStringId<'MailDocumentId'>;

// ============================================================================
// Delivery state
// ============================================================================

/**
 * All valid delivery state strings, in typical lifecycle order.
 * Managed exclusively by the Firebase Trigger Email extension.
 * See https://firebase.google.com/docs/extensions/official/firestore-send-email/delivery-status
 */
export const SUPPORTED_DELIVERY_STATES = [
  'PENDING',
  'PROCESSING',
  'SUCCESS',
  'ERROR',
  'RETRY',
] as const;

/**
 * The status of an email delivery, managed by the Firebase Trigger Email
 * extension.
 *
 * An email will typically go from `PENDING` to `PROCESSING` to either
 * `SUCCESS` or `ERROR`. Once in a terminal state (`SUCCESS` or `ERROR`),
 * further changes to the document will not trigger another send. To resend
 * with document updates, set `state` back to `PENDING` or `RETRY`.
 *
 * See https://firebase.google.com/docs/extensions/official/firestore-send-email/delivery-status
 */
export type DeliveryState = (typeof SUPPORTED_DELIVERY_STATES)[number];

/**
 * Zod schema for {@link DeliveryState}. Validates that a value is one of the
 * five valid delivery state strings.
 */
export const DeliveryStateSchema = z.enum(SUPPORTED_DELIVERY_STATES);

/**
 * Type guard for {@link DeliveryState}.
 *
 * @param data - The value to test.
 * @return `true` when `data` is a valid `DeliveryState` string.
 */
export function isDeliveryState(data: unknown): data is DeliveryState {
  return DeliveryStateSchema.safeParse(data).success;
}

// ============================================================================
// Email attachment
// ============================================================================

/**
 * Zod schema for {@link EmailAttachment}.
 *
 * The `content` field accepts a `string` (Base64-encoded or UTF-8 body) or a
 * `Uint8Array` (binary). **Important**: `Uint8Array` values do NOT survive
 * `JSON.stringify` — they must be Base64/UTF-8 encoded before crossing Cloud
 * Tasks task boundaries. Callers must use `content` as a string or `href` for
 * any record that travels through the email-queue pipeline.
 *
 * At least one of `content`, `href`, or `raw` must be provided; the schema
 * enforces this with a `.refine()` check.
 */
export const EmailAttachmentSchema = z
  .object({
    /**
     * Filename reported as the name of the attached file.
     * Unicode is allowed. Required.
     */
    filename: z.string(),
    /**
     * Blob of data to attach. Base64 or UTF-8 string, or a `Uint8Array` for
     * in-process use. One of `content`, `href`, or `raw` is required.
     *
     * Note: `Uint8Array` does not survive JSON.stringify; use a string or
     * `href` when the record crosses a Cloud Tasks boundary.
     */
    content: z.union([z.string(), z.instanceof(Uint8Array)]).nullish(),
    /**
     * URL of a remote file to attach. One of `content`, `href`, or `raw` is
     * required.
     */
    href: z.string().nullish(),
    /**
     * Optional MIME content type. When omitted, derived from `filename`.
     */
    content_type: z.string().nullish(),
    /**
     * Overrides the entire contents of the current MIME node, including MIME
     * headers. Useful when you prepare node contents yourself. One of
     * `content`, `href`, or `raw` is required.
     */
    raw: z.string().nullish(),
    /**
     * Optional content disposition type, defaults to `"attachment"`.
     * Other options: `"inline"`, `"form-data"`.
     * See https://developer.mozilla.org/en-US/docs/Web/HTTP/Reference/Headers/Content-Disposition
     */
    content_disposition: z.string().nullish(),
  })
  .refine(
    data =>
      data.content !== null && data.content !== undefined && data.content !== ''
        ? true
        : (data.href !== null && data.href !== undefined && data.href !== '') ||
          (data.raw !== null && data.raw !== undefined && data.raw !== ''),
    {message: 'At least one of content, href, or raw must be provided'},
  );

/** A file to be attached to an email. See https://nodemailer.com/message/attachments */
export type EmailAttachment = z.infer<typeof EmailAttachmentSchema>;

/**
 * Type guard for {@link EmailAttachment}.
 *
 * @param data - The value to test.
 * @return `true` when `data` satisfies the `EmailAttachment` schema.
 */
export function isEmailAttachment(data: unknown): data is EmailAttachment {
  return EmailAttachmentSchema.safeParse(data).success;
}

// ============================================================================
// Mail document schema factory
// ============================================================================

/**
 * Shared JSON-object schema for ad-hoc payload fields.
 * Equivalent to `JSONObject` — a record whose values may be any JSON-safe type.
 */
const JsonObjectSchema = z.record(z.string(), z.unknown());

/**
 * Shared envelope fields present on both the caller-writeable
 * {@link MailDocumentInputSchema} and the persisted
 * {@link MailDocumentSchema}. Defining them once here eliminates the
 * duplicated source of truth between the two schemas.
 *
 * Each field carries its canonical TSDoc comment so IDE intellisense and
 * generated documentation remain accurate regardless of which schema
 * a consumer reads.
 */
const mailEnvelopeShape = {
  /**
   * Recipient email address or list of addresses.
   * The extension delivers to all addresses in the array.
   */
  to: z.union([z.string(), z.array(z.string())]),
  /**
   * Sender address, formatted as `"Name <email@example.com>"`.
   * Overrides the extension's default from-address and any template
   * `overridingFromAddress` field.
   */
  from: z.string().optional(),
  /**
   * Carbon-copy address or list of addresses.
   */
  cc: z.union([z.string(), z.array(z.string())]).optional(),
  /**
   * Blind-carbon-copy address or list of addresses.
   */
  bcc: z.union([z.string(), z.array(z.string())]).optional(),
  /**
   * Template reference for a templated send. Provide EITHER `template`
   * (templated send via the extension's Handlebars engine) OR `message`
   * (pre-rendered content for the Emails V2 pre-render engine). All
   * current writers use `template`; `message` provides a typed home for
   * future pre-rendered sends (Emails V2 epic #1906).
   *
   * See https://firebase.google.com/docs/extensions/official/firestore-send-email/templates
   */
  template: z
    .object({
      /**
       * Template name. Corresponds to the document id in the
       * `email-templates` collection consumed by the extension.
       */
      name: z.string(),
      /**
       * Handlebars template data. Keys are substituted into `{{key}}`
       * placeholders in the template.
       */
      data: JsonObjectSchema.optional(),
    })
    .optional(),
  /**
   * Pre-rendered message content for direct (non-templated) sends.
   * Provide EITHER `template` (templated send) OR `message`
   * (pre-rendered content). Modeling both gives a future pre-render
   * engine a typed home (Emails V2 epic #1906). Do not set both — the
   * extension's behavior when both fields are present is undefined.
   *
   * All current writers use `template`; this field is reserved for the
   * Emails V2 pre-render path.
   */
  message: z
    .object({
      /** Email subject line. */
      subject: z.string().optional(),
      /** Plain-text body. Sent to clients that do not render HTML. */
      text: z.string().nullish(),
      /** HTML body. Sent to clients that support HTML. */
      html: z.string().nullish(),
      /** Optional list of file attachments. */
      attachments: z.array(EmailAttachmentSchema).nullish(),
    })
    .loose()
    .optional(),
} as const;

/**
 * The **privileged** reply-threading envelope fields.
 *
 * They are deliberately NOT part of {@link mailEnvelopeShape}, because that
 * shape is spread into {@link MailDocumentInputSchema} — which is not merely a
 * pipeline-ownership boundary but the request-validation allowlist for the
 * enqueue callables (see `enqueueAdminEmails` and `api.ts` in
 * `@cip/functions`). Those callables gate on a per-course admin role and then
 * spread the caller's record straight onto the `/mail` document, so anything
 * this shape admits there is deliverable. `replyTo` in particular is not
 * DMARC-checked, so accepting it from a callable would let a course admin send
 * DMARC-aligned CIP-branded mail whose replies route to an address of their
 * choosing.
 *
 * Instead they live on the persisted {@link MailDocumentSchema} (for read-path
 * typing) and on {@link MailDocumentAgentInputSchema} (the one write surface
 * that needs them — the email agent's reply path). Keeping the grant an
 * allowlist rather than a denylist means a future callable that spreads
 * `mailEnvelopeShape` defaults to *not* accepting these fields.
 */
const privilegedReplyShape = {
  /**
   * Address replies should be directed to, formatted as
   * `"Name <email@example.com>"`. When omitted, replies go to whatever `from`
   * resolves to.
   *
   * Honoured by the deployed Trigger Email extension — this is the extension's
   * first-class field, verified in prod. See the "From vs Reply-To split"
   * section of `src/apps/functions/src/emails/replies/README.md` for why the
   * reply path sets it rather than putting `Reply-To` in `headers`.
   */
  replyTo: z.string().optional(),
  /**
   * Additional RFC 822 header name-to-value pairs requested on the outgoing
   * message. The email agent uses it to stitch a reply into an existing
   * conversation via `In-Reply-To` and `References`.
   *
   * **Best-effort only**: the currently deployed Trigger Email extension does
   * not forward this map, so headers placed here are dropped at send time.
   * They are persisted regardless, and take effect if the extension's header
   * passthrough is later enabled.
   *
   * `message.headers` and `message.messageId` are dropped too — verified in
   * prod 2026-09-14 by reading the delivered message's DKIM `h=` list, which
   * enumerates exactly what the relay held at signing time. So there is no
   * alternative field to move threading headers to, and no way to mint our own
   * `Message-ID`; the reply path reconstructs the chain after the fact instead.
   * See `src/apps/functions/src/emails/replies/README.md`. Do not re-run the
   * experiment.
   *
   * Values are strings because that is what a header carries on the wire; a
   * narrower type than the ad-hoc `JsonObjectSchema` used for template data,
   * which would admit values a header cannot represent.
   */
  headers: z.record(z.string(), z.string()).optional(),
  /**
   * Where the reply this document sends belongs in the email agent's data
   * model: the bucket and email document of the *outbound copy* the reply path
   * wrote, plus the conversation it joins.
   *
   * Carried on the `/mail` document because the extension only ever writes
   * back to this document, and the Message-ID it assigns arrives after the
   * send (in `delivery.info.messageId`). The delivery trigger needs somewhere
   * to put that id and something to point a thread index entry at, and this is
   * the only place both are reachable without a query — see
   * `captureReplyMessageId` in `@cip/functions`.
   *
   * Absent on every non-reply `/mail` document (campaigns, confirmations,
   * queue sends), which is what the trigger gates on.
   */
  replyContext: z
    .object({
      /**
       * Bucket holding the outbound copy — a course id, or the bulk bucket id
       * (`UNCATEGORIZED_BUCKET_ID` in `./reportBucket`).
       */
      bucketId: z.string(),
      /**
       * Document id of the outbound copy under
       * `reports/{bucketId}/emails`. This is the copy of the reply we wrote,
       * not the message being replied to.
       */
      emailId: z.string(),
      /**
       * Conversation the reply joins, or `null` when the message being
       * replied to had no derivable conversation id (`deriveThreadId` in the
       * functions `pollInboxLib` can return `null`). `null` means there is
       * nothing for a thread index entry to point at.
       */
      threadId: z.string().nullable(),
    })
    .strict()
    .optional(),
} as const;

/**
 * Factory that builds the `MailDocument` Zod schema with a caller-supplied
 * timestamp schema. Allows the admin SDK variant (`admin/mailDocument.ts`) to
 * swap in `AdminTimestampSchema` so delivery timestamps are transformed to
 * `Timestamp` instances on read.
 *
 * @param timestampSchema - Zod schema for Firestore timestamp fields.
 * @return The full `MailDocument` object schema (before `.loose()`).
 */
export function makeMailDocumentSchema<TTimestamp>(
  timestampSchema: z.ZodType<TTimestamp>,
) {
  return z.object({
    ...mailEnvelopeShape,
    ...privilegedReplyShape,
    /**
     * Delivery tracking managed by the Firebase Trigger Email extension.
     * See https://firebase.google.com/docs/extensions/official/firestore-send-email/delivery-status
     *
     * Writers should NOT set this field — it is written and managed
     * exclusively by the extension after the document is created.
     */
    delivery: z
      .object({
        /**
         * Current delivery state. Transitions:
         * `PENDING → PROCESSING → SUCCESS | ERROR`.
         * Set to `PENDING` or `RETRY` to trigger a re-send.
         */
        state: DeliveryStateSchema.optional(),
        /**
         * Timestamp when the extension began processing this document.
         */
        startTime: timestampSchema.optional(),
        /**
         * Timestamp when delivery reached a terminal state (`SUCCESS` or
         * `ERROR`).
         */
        endTime: timestampSchema.optional(),
        /**
         * Expiry deadline for a `PROCESSING` state. When the current time
         * exceeds this value the extension treats the delivery as a hang.
         */
        leaseExpireTime: timestampSchema.nullish(),
        /**
         * Human-readable error message when `state === 'ERROR'`.
         */
        error: z.string().nullish(),
        /**
         * Number of delivery attempts so far.
         */
        attempts: z.number().optional(),
        /**
         * SMTP outcome information. Present when at least one recipient was
         * accepted for delivery.
         */
        info: z
          .object({
            /**
             * Extension-managed SMTP message ID.
             */
            messageId: z.string().optional(),
            /**
             * Recipients successfully accepted by the SMTP server.
             */
            accepted: z.array(z.string()).optional(),
            /**
             * Recipients rejected by the SMTP server.
             */
            rejected: z.array(z.string()).optional(),
            /**
             * Recipients temporarily rejected (will be retried by SMTP).
             */
            pending: z.array(z.string()).optional(),
            /**
             * Last SMTP response string from the server.
             */
            response: z.string().optional(),
          })
          .loose()
          .optional(),
      })
      .loose()
      .optional(),
    /**
     * Code in Place metadata attached by the producer (the Cloud Tasks
     * worker or direct sendEmail callers). Used for batch attribution,
     * course scoping, and per-job tracking in admin dashboards.
     */
    cipMetadata: z
      .object({
        /**
         * The course this email belongs to. Used for Firestore security
         * rules scoping and admin dashboard grouping.
         */
        courseId: z.custom<CourseId>(),
        /**
         * The `email-batches` document id for the batch this email belongs
         * to. Present when the email was sent via `sendEmailBatch`.
         * System-managed — not accepted on the caller-writeable
         * {@link MailDocumentInput}.
         */
        batchId: z.custom<EmailBatchId>().optional(),
        /**
         * Firestore id of the parent `EmailSendJob` doc. Written by the
         * Cloud Tasks worker when the send is part of a tracked job.
         * System-managed — not accepted on the caller-writeable
         * {@link MailDocumentInput}.
         */
        jobId: z.string().optional(),
        /**
         * Zero-based index of this record within the original
         * `EnqueueEmailsRequest.records` array. Written by the Cloud Tasks
         * worker alongside `jobId` so the delivery-state Firestore trigger
         * can attribute outcomes to the correct `EmailSendJob` item.
         * System-managed — not accepted on the caller-writeable
         * {@link MailDocumentInput}.
         */
        itemIndex: z.number().int().nonnegative().optional(),
        /**
         * Firestore id of the `email-campaigns` document this send belongs
         * to, when the send was enqueued into a campaign. Written by the
         * Cloud Tasks worker alongside `jobId`; the delivery-state trigger
         * reads it to release the recipient's campaign claim if delivery
         * terminally fails. System-managed — not accepted on the
         * caller-writeable {@link MailDocumentInput}; callers name their
         * campaign via `EnqueueEmailsRequest.campaignId` instead.
         */
        campaignId: z.string().optional(),
        /**
         * Ad-hoc producer-specific tracking payload. Not runtime-validated
         * on the read path — use for fields (e.g. reminder variant, section
         * id) that do not justify their own typed slot. Treat as JSONObject
         * so values stay Firestore-compatible.
         */
        additionalFields: JsonObjectSchema.optional(),
      })
      .loose()
      .optional(),
  });
}

// ============================================================================
// Caller-writeable input schema
// ============================================================================

/**
 * Zod schema for the caller-writeable surface of a `mail` document.
 *
 * Contains only the envelope fields that callers are permitted to supply:
 * `to`, `from`, `cc`, `bcc`, `template`, `message`, and a restricted
 * `cipMetadata` limited to `{courseId, additionalFields?}`.
 *
 * This schema is also the **request-validation allowlist** for the enqueue
 * callables (`enqueueAdminEmails` and the `isMailDocumentInput` gate in
 * `api.ts`, both in `@cip/functions`), which spread a validated record onto the
 * `/mail` document. Adding a field here grants every per-course admin the
 * ability to set it on real outbound mail, so treat additions as a privilege
 * grant. The privileged reply fields are kept off this schema for exactly that
 * reason — see {@link MailDocumentAgentInputSchema}.
 *
 * **Strict rejection of system-managed fields**: the outer object is strict
 * (`.strict()`) so top-level `delivery.*` is rejected with a Zod error. The
 * `cipMetadata` sub-object is also strict so `batchId`, `jobId`,
 * `itemIndex`, and `campaignId` — which are written by the pipeline worker and
 * `sendEmailBatch`
 * — are rejected at the enqueue boundary. Callers that set any of these
 * fields will receive a validation error rather than silently overriding
 * system-managed state.
 *
 * `template` and `message` inner objects are kept permissive (not strict) to
 * avoid blocking unknown extension-side fields on those sub-objects.
 *
 * @see {@link MailDocument} for the persisted form that includes
 *   `delivery.*` and `cipMetadata.{batchId, jobId, itemIndex, campaignId}`.
 */
export const MailDocumentInputSchema = z
  .object({
    ...mailEnvelopeShape,
    /**
     * Caller-controlled Code in Place metadata. Limited to `courseId` and
     * `additionalFields`. System-managed fields (`batchId`, `jobId`,
     * `itemIndex`, `campaignId`) are not accepted here — they are written by the pipeline
     * after the caller record is enqueued. Passing them will produce a Zod
     * validation error at the enqueue boundary.
     */
    cipMetadata: z
      .object({
        /**
         * The course this email belongs to. Used for Firestore security
         * rules scoping and admin dashboard grouping.
         */
        courseId: z.custom<CourseId>(),
        /**
         * Ad-hoc producer-specific tracking payload. Treat as JSONObject
         * so values stay Firestore-compatible.
         */
        additionalFields: JsonObjectSchema.optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

/**
 * TypeScript type for the caller-writeable surface of a `mail` document.
 * Inferred from {@link MailDocumentInputSchema}.
 *
 * Use this type when constructing records to pass to `enqueueEmails`,
 * `sendEmail`, `sendEmailBatch`, or any other entry point that writes to the
 * `/mail` collection. The type intentionally omits `delivery.*` and
 * `cipMetadata.{batchId, jobId, itemIndex, campaignId}` — those are
 * system-managed fields
 * written by the pipeline after the document is created.
 *
 * @see {@link MailDocument} for the persisted form.
 */
export type MailDocumentInput = z.infer<typeof MailDocumentInputSchema>;

/**
 * Type guard for {@link MailDocumentInput}. Returns `true` when `data`
 * satisfies the caller-writeable input schema (strict — rejects `delivery`
 * and system-managed `cipMetadata` fields).
 *
 * @param data - The value to test.
 * @return `true` when `data` is a valid `MailDocumentInput`.
 */
export function isMailDocumentInput(data: unknown): data is MailDocumentInput {
  return MailDocumentInputSchema.safeParse(data).success;
}

// ============================================================================
// Privileged agent input schema
// ============================================================================

/**
 * Zod schema for the email agent's reply write surface: everything
 * {@link MailDocumentInputSchema} accepts, plus the privileged
 * reply-threading fields in `privilegedReplyShape`. Still `.strict()`, so
 * `delivery.*` and system-managed `cipMetadata` fields remain rejected.
 *
 * **Why this is separate.** `replyTo` and `headers` are deliberately absent
 * from `MailDocumentInputSchema` because that schema validates untrusted
 * callable input: the enqueue callables gate on a per-course admin role and
 * then spread the record onto `/mail`, and `replyTo` is not DMARC-checked, so
 * admitting it there would make replies to CIP-branded mail redirectable by any
 * course admin. Only the email agent's own reply path — which chooses the
 * address itself rather than taking it from a request — writes these fields, so
 * only it uses this schema.
 *
 * Do not use this type to annotate a callable request field. If a new write
 * path needs these fields, give it its own narrow surface rather than widening
 * `MailDocumentInputSchema`.
 *
 * @see {@link MailDocumentInputSchema} for the untrusted-caller surface.
 */
export const MailDocumentAgentInputSchema =
  MailDocumentInputSchema.extend(privilegedReplyShape).strict();

/**
 * TypeScript type for the email agent's reply write surface.
 * Inferred from {@link MailDocumentAgentInputSchema}.
 *
 * Use this instead of {@link MailDocumentInput} when constructing a `/mail`
 * record that sets any of the privileged reply fields. Everything else should
 * keep using `MailDocumentInput`.
 */
export type MailDocumentAgentInput = z.infer<
  typeof MailDocumentAgentInputSchema
>;

/**
 * Type guard for {@link MailDocumentAgentInput}. Returns `true` when `data`
 * satisfies the agent input schema (strict — accepts the privileged reply
 * fields, still rejects `delivery` and system-managed `cipMetadata` fields).
 *
 * @param data - The value to test.
 * @return `true` when `data` is a valid `MailDocumentAgentInput`.
 */
export function isMailDocumentAgentInput(
  data: unknown,
): data is MailDocumentAgentInput {
  return MailDocumentAgentInputSchema.safeParse(data).success;
}

// ============================================================================
// SDK-agnostic schema and type
// ============================================================================

/**
 * SDK-agnostic Zod schema for a `mail` collection document.
 *
 * This is the **persisted** form of the document — a structural superset of
 * {@link MailDocumentInputSchema}. It adds:
 * - `delivery.*` — written and managed by the Firebase Trigger Email
 *   extension after the document is created.
 * - `cipMetadata.batchId` — written by `sendEmailBatch`.
 * - `cipMetadata.jobId` — written by the Cloud Tasks worker when the send
 *   is part of a tracked `EmailSendJob`.
 * - `cipMetadata.itemIndex` — written by the Cloud Tasks worker alongside
 *   `jobId` so the delivery-state trigger can attribute outcomes.
 * - `cipMetadata.campaignId` — written by the Cloud Tasks worker so the
 *   delivery-state trigger can release a campaign claim on terminal failure.
 * - the privileged reply fields in `privilegedReplyShape`, declared here so
 *   read paths get types for them. `.loose()` already tolerated them; this is
 *   typing, not validation. Only {@link MailDocumentAgentInputSchema} accepts
 *   them on a write surface.
 *
 * Timestamp fields use {@link FirestoreTimestampSchema}, which accepts both
 * Timestamp instances and the JSON-serialized `{_seconds, _nanoseconds}` form.
 * The admin SDK variant (`@cip/core/firebase/admin`) extends this with
 * `AdminTimestampSchema` so timestamps are transformed to `Timestamp`
 * instances on read.
 *
 * Use `.loose()` so future extension-managed fields on the document do not
 * cause parse failures.
 */
export const MailDocumentSchema = makeMailDocumentSchema(
  FirestoreTimestampSchema,
).loose();

/**
 * TypeScript type inferred from {@link MailDocumentSchema}.
 * This is the **persisted** form — a structural superset of
 * {@link MailDocumentInput}. Includes `delivery.*` and
 * `cipMetadata.{batchId, jobId, itemIndex, campaignId}`.
 *
 * Use `MailDocumentAdmin` (from `@cip/core/firebase/admin`) in admin-SDK
 * contexts where you need `Timestamp` instances on the delivery fields.
 */
export type MailDocument = z.infer<typeof MailDocumentSchema>;

/**
 * Type guard for {@link MailDocument}. Returns `true` when `data` satisfies
 * the full `MailDocumentSchema`.
 *
 * @param data - The value to test.
 * @return `true` when `data` is a valid `MailDocument`.
 */
export function isMailDocument(data: unknown): data is MailDocument {
  return MailDocumentSchema.safeParse(data).success;
}

// ============================================================================
// Path helpers
// ============================================================================

/**
 * Returns the Firestore collection path for the `mail` collection.
 *
 * @return The string `"mail"`.
 */
export function getMailCollectionPath(): string {
  return MAIL_COLLECTION;
}

/**
 * Returns the Firestore document path for a specific mail document.
 *
 * @param params - Object containing the mail document id.
 * @param params.mailDocId - The document id within the `mail` collection.
 * @return A path string of the form `"mail/{mailDocId}"`.
 */
export function getMailDocumentPath({mailDocId}: {mailDocId: string}): string {
  return `${MAIL_COLLECTION}/${mailDocId}`;
}
